import { type CSSProperties, memo } from "react";
import type { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { currencyFormatter } from "@common/helpers/currency";
import type { View } from "@common/types/views/views";
import { ViewDensity } from "@common/types/views/views";
import type { CellContextWithDragHandle } from "@design-system/ViewTable/ViewTable";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { DragHandle6DotIcon } from "@icons";
import styles from "../ViewTablePlayground.module.scss";

export const SELECTION_HINT =
    "Click a checkbox to select a row, or ⌘/Ctrl-click a row to toggle it";

export const DENSITY_OPTIONS = [
    { value: ViewDensity.SM, label: "Comfortable" },
    { value: ViewDensity.MD, label: "Default" },
    { value: ViewDensity.LG, label: "Compact" },
] satisfies ReadonlyArray<{ value: ViewDensity; label: string }>;

export interface BusinessRecordRow {
    id: "northstar" | "harbor" | "juniper" | "summit" | "cedar" | "atlas";
    name: string;
    owner: string;
    category: string;
    dueDate: string;
    status: "Ready" | "In review" | "Waiting";
    amount: number;
}

export interface ReorderRow {
    id: "collect" | "review" | "approve" | "publish" | "archive";
    name: string;
    owner: string;
}

export interface NestedShowcaseRow {
    id:
        | "operations"
        | "month-end"
        | "reconcile-bank"
        | "review-accruals"
        | "reporting"
        | "publish-statements"
        | "archive-package";
    name: string;
    kind: "group" | "item";
    children: NestedShowcaseRow[];
}

export const BUSINESS_RECORDS: BusinessRecordRow[] = [
    {
        id: "northstar",
        name: "Northstar Studio",
        owner: "Maya",
        category: "Design",
        dueDate: "Aug 20",
        status: "Ready",
        amount: 4280,
    },
    {
        id: "harbor",
        name: "Harbor Goods",
        owner: "Noah",
        category: "Retail",
        dueDate: "Aug 22",
        status: "In review",
        amount: 1975,
    },
    {
        id: "juniper",
        name: "Juniper Labs",
        owner: "Priya",
        category: "Research",
        dueDate: "Aug 24",
        status: "Waiting",
        amount: 6320,
    },
    {
        id: "summit",
        name: "Summit Works",
        owner: "Leo",
        category: "Services",
        dueDate: "Aug 27",
        status: "Ready",
        amount: 3540,
    },
    {
        id: "cedar",
        name: "Cedar House",
        owner: "Ava",
        category: "Hospitality",
        dueDate: "Aug 29",
        status: "In review",
        amount: 2860,
    },
    {
        id: "atlas",
        name: "Atlas Supply",
        owner: "Eli",
        category: "Wholesale",
        dueDate: "Aug 31",
        status: "Waiting",
        amount: 5180,
    },
];

export const REORDER_ROWS: ReorderRow[] = [
    { id: "collect", name: "Collect records", owner: "Maya" },
    { id: "review", name: "Review details", owner: "Noah" },
    { id: "approve", name: "Approve changes", owner: "Priya" },
    { id: "publish", name: "Publish report", owner: "Leo" },
    { id: "archive", name: "Archive package", owner: "Ava" },
];

export const NESTED_ROWS: NestedShowcaseRow[] = [
    {
        id: "operations",
        name: "Operations",
        kind: "group",
        children: [
            {
                id: "month-end",
                name: "Month-end",
                kind: "group",
                children: [
                    {
                        id: "reconcile-bank",
                        name: "Reconcile bank",
                        kind: "item",
                        children: [],
                    },
                    {
                        id: "review-accruals",
                        name: "Review accruals",
                        kind: "item",
                        children: [],
                    },
                ],
            },
        ],
    },
    {
        id: "reporting",
        name: "Reporting",
        kind: "group",
        children: [
            {
                id: "publish-statements",
                name: "Publish statements",
                kind: "item",
                children: [],
            },
            {
                id: "archive-package",
                name: "Archive package",
                kind: "item",
                children: [],
            },
        ],
    },
];

const cloneNestedRow = (row: NestedShowcaseRow): NestedShowcaseRow => ({
    ...row,
    children: row.children.map(cloneNestedRow),
});

export const createReorderRows = (): ReorderRow[] =>
    REORDER_ROWS.map((row) => ({ ...row }));

export const createNestedRows = (): NestedShowcaseRow[] =>
    NESTED_ROWS.map(cloneNestedRow);

const ALIGN_RIGHT: CSSProperties = {
    width: "100%",
    textAlign: "right",
};

export const createNameCell = <TData extends { name: string }>() =>
    memo(function NameCell({
        row,
    }: CellContext<ViewTableFeatures, TData, TData["name"]>) {
        return <ViewTableCell>{row.original.name}</ViewTableCell>;
    });

export const createOwnerCell = <TData extends { owner: string }>() =>
    memo(function OwnerCell({
        row,
    }: CellContext<ViewTableFeatures, TData, TData["owner"]>) {
        return <ViewTableCell>{row.original.owner}</ViewTableCell>;
    });

export const BusinessNameCell = createNameCell<BusinessRecordRow>();

export const BusinessOwnerCell = createOwnerCell<BusinessRecordRow>();

export const BusinessCategoryCell = memo(function BusinessCategoryCell({
    row,
}: CellContext<
    ViewTableFeatures,
    BusinessRecordRow,
    BusinessRecordRow["category"]
>) {
    return <ViewTableCell>{row.original.category}</ViewTableCell>;
});

export const BusinessDueDateCell = memo(function BusinessDueDateCell({
    row,
}: CellContext<
    ViewTableFeatures,
    BusinessRecordRow,
    BusinessRecordRow["dueDate"]
>) {
    return <ViewTableCell>{row.original.dueDate}</ViewTableCell>;
});

export const BusinessStatusCell = memo(function BusinessStatusCell({
    row,
}: CellContext<
    ViewTableFeatures,
    BusinessRecordRow,
    BusinessRecordRow["status"]
>) {
    return <ViewTableCell>{row.original.status}</ViewTableCell>;
});

export const BusinessAmountCell = memo(function BusinessAmountCell({
    row,
}: CellContext<
    ViewTableFeatures,
    BusinessRecordRow,
    BusinessRecordRow["amount"]
>) {
    return (
        <ViewTableCell style={ALIGN_RIGHT}>
            {currencyFormatter.format(row.original.amount)}
        </ViewTableCell>
    );
});

export const mergePlaygroundView = ({
    currentView,
    nextView,
}: {
    currentView: View;
    nextView: View;
}): View => ({
    ...currentView,
    ...nextView,
    filters: currentView.filters,
    options: {
        ...currentView.options,
        ...nextView.options,
    },
});

export const createDragHandleCell = <TData extends { name: string }>() =>
    memo(function DragHandleCell({
        row,
        rowDragHandle,
    }: CellContextWithDragHandle<TData, TData["name"]>) {
        if (!rowDragHandle) {
            return null;
        }

        return (
            <button
                type="button"
                aria-label={`Drag ${row.original.name}`}
                className={styles.dragHandle}
                onClick={(event) => event.stopPropagation()}
                {...rowDragHandle.attributes}
                {...rowDragHandle.listeners}
            >
                <DragHandle6DotIcon aria-hidden />
            </button>
        );
    });
